/**
 * Project Inventory Panel
 * Floating collapsible panel in the bottom-left of the project map
 * showing asset counts grouped by type with sprite icons.
 * Matches the original AsseTrac design.
 *
 * ASS-77: adds a MAT (manual) row with one sub-row per Mat Type, summed
 * from state.count.entriesByProject[projectId] -- never /counts, which
 * ASS-64 doesn't build. Reads projectId from the route via useParams()
 * rather than a new prop threaded through ProjectPage.jsx, since this
 * component already sits under the /projects/:id route. Manual mats are
 * summed into one MAT (manual) total but never merged into any beacon or
 * equipment count -- they render as a separate top-level row, parallel to
 * (not inside) the existing asset typeGroups.
 *
 * AST-118: Enter Asset Move entries carry manualAssetId, so the MAT (manual)
 * sub-rows count by manual asset name (ST124). Legacy and IVR entries with
 * only a subtype -- or an asset id that's no longer known -- fall back to the
 * subtype name and are never dropped.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { entryProjectDelta } from '../../utils/locationCounts';
import { scoreboardRows, rowTooltip } from '../../utils/scoreboardRows';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ChevronRight as ChevronIcon,
} from '@mui/icons-material';

import SpriteIcon from './SpriteIcon';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchEntries } from '../../store/slices/countSlice';
import { fetchProjectManualAssets } from '../../services/manualAssetsApi';
import { colorPalette, SPACING } from '../../theme';

const ProjectInventoryPanel = ({ assets = [] }) => {
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();

  // ASS-77 hold-fix: load saved entries on mount (hard reload, or navigating
  // back to the project) so the MAT (manual) row reflects the server. Gated
  // on manual/hybrid: the API returns 403 on entries for beacon-only customers.
  const currentProject = useAppSelector((state) => state.project.currentProject);
  const isManualLike = currentProject?.trackingMode === 'manual' || currentProject?.trackingMode === 'hybrid';
  useEffect(() => {
    if (projectId && isManualLike) {
      dispatch(fetchEntries({ projectId, filters: {} }));
    }
  }, [projectId, isManualLike, dispatch]);

  // ASS-69 follow-up: persist collapse state across reloads (N8 requirement).
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem('assetrac-inventory-panel-open');
      return stored === null ? true : stored === 'true';
    } catch { return true; }
  });
  const [expandedTypes, setExpandedTypes] = useState({});
  const { assetTypes, assetSubTypes } = useAppSelector((state) => state.asset);

  // ASS-77: entries live only under state.count.entriesByProject[projectId]
  // -- never /counts. This selector alone (no dispatch/fetch) is enough:
  // whichever surface wrote the entry (AddInventoryDialog via
  // createEntryAction) already put it in this same slice, so a Save
  // updates this panel immediately, no reload needed.
  const entriesRaw = useAppSelector((state) => state.count.entriesByProject[projectId]);

  // AST-118: manual asset names (archived included, so historical entries
  // are still named). Non-fatal: on failure rows fall back to subtype names.
  // Refetched (once per id) when an entry names an asset we don't know yet,
  // e.g. one set up after this panel mounted.
  // AST-117: the full records (subtype, description, archived) -- the rows
  // are ordered by subtype, archived ones are labelled, and the tooltip shows
  // the description. Keyed by id.
  const [manualAssetById, setManualAssetById] = useState({});
  const triedAssetIdsRef = useRef(new Set());
  const unknownAssetKey = useMemo(() => {
    const ids = new Set();
    for (const e of (entriesRaw || [])) {
      if (e?.manualAssetId && !manualAssetById[e.manualAssetId]
        && !triedAssetIdsRef.current.has(e.manualAssetId)) ids.add(e.manualAssetId);
    }
    return [...ids].sort().join(',');
  }, [entriesRaw, manualAssetById]);
  // The project the names belong to; a response for any other is ignored.
  const namesForRef = useRef(null);
  useEffect(() => {
    if (!projectId || !isManualLike) return;
    if (namesForRef.current !== projectId) {
      namesForRef.current = projectId;
      triedAssetIdsRef.current = new Set();
      setManualAssetById((prev) => (Object.keys(prev).length ? {} : prev));
    } else if (!unknownAssetKey) {
      return; // already loaded and every entry's asset is known
    }
    for (const id of unknownAssetKey.split(',').filter(Boolean)) triedAssetIdsRef.current.add(id);
    const forProject = projectId;
    fetchProjectManualAssets(forProject)
      .then((res) => {
        if (namesForRef.current !== forProject) return;
        const byId = {};
        for (const a of (res?.items || [])) if (a?.id && a.name) byId[a.id] = a;
        setManualAssetById(byId);
      })
      .catch(() => { /* fall back to subtype names */ });
  }, [projectId, isManualLike, unknownAssetKey]);

  // ASS-64: with the localStorage fallback gone, a failed load must be visible
  // -- otherwise an empty MAT row reads as "no mats" when it means "unknown".
  const entriesError = useAppSelector((state) => state.count.entriesError);

  // Build lookup maps for type/subtype names
  const typeMap = useMemo(() => {
    const map = {};
    for (const t of (assetTypes || [])) {
      map[t.id] = t;
    }
    return map;
  }, [assetTypes]);

  const subtypeMap = useMemo(() => {
    const map = {};
    for (const s of (assetSubTypes || [])) {
      map[s.id] = s;
    }
    return map;
  }, [assetSubTypes]);

  // Group assets by type and count
  const typeGroups = useMemo(() => {
    const groups = {};
    for (const asset of assets) {
      // Resolve type name: try type object, then lookup by assetTypeId
      const resolvedType = asset.type || typeMap[asset.assetTypeId] || {};
      const typeName = resolvedType.name || asset.assetTypeName || 'Unknown';
      const iconName = resolvedType.iconName || '';

      // Resolve subtype name
      const resolvedSubtype = asset.subType || subtypeMap[asset.assetSubTypeId] || {};
      const subtypeName = resolvedSubtype.name || '';

      if (!groups[typeName]) {
        groups[typeName] = { name: typeName, iconName, count: 0, subtypes: {} };
      }
      groups[typeName].count++;

      if (subtypeName) {
        if (!groups[typeName].subtypes[subtypeName]) {
          groups[typeName].subtypes[subtypeName] = 0;
        }
        groups[typeName].subtypes[subtypeName]++;
      }
    }

    // Sort by count descending
    return Object.values(groups).sort((a, b) => b.count - a.count);
  }, [assets, typeMap, subtypeMap]);

  // ASS-77: MAT (manual) group. Sums entries' signed quantity, from
  // assetSubTypes under the "Mat" parent type (resolved by name, never
  // hard-coded).
  //
  // AST-117: rows are now one per MANUAL ASSET with counts (ST124 · 1200),
  // ordered by subtype then name, with unassigned counts kept as a subtype
  // row in that subtype's place. The rules live in utils/scoreboardRows.
  // Still: an entry whose subtype isn't in the catalog counts as "Unknown
  // type" (AST-100), and From+To moves net to 0 (AST-90/88) -- the same
  // rule as utils/locationCounts, so this total and See Mat Locations agree.
  const matGroup = useMemo(() => {
    const matType = (assetTypes || []).find((t) => t.name === 'Mat');
    if (!matType) return null;

    // Today's subtype order -- the order the catalog returns.
    const matSubtypes = (assetSubTypes || []).filter((s) => s.typeId === matType.id);
    const { total, rows } = scoreboardRows(
      entriesRaw || [], matSubtypes, Object.values(manualAssetById), entryProjectDelta
    );
    if (rows.length === 0) return null;

    return { name: 'MAT (manual)', count: total, rows };
  }, [entriesRaw, assetTypes, assetSubTypes, manualAssetById]);

  const toggleType = (typeName) => {
    setExpandedTypes((prev) => ({ ...prev, [typeName]: !prev[typeName] }));
  };

  // Always show the panel, even if there are no assets -- ASS-77 also
  // counts as "has content" so the MAT (manual) row can appear even on a
  // Manual project with zero beacon assets.
  const showEntriesError = isManualLike && !!entriesError;
  const hasAssets = typeGroups.length > 0 || !!matGroup || showEntriesError;

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        zIndex: 10,
        width: 220,
        backgroundColor: colorPalette.backgroundColor,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 2,
          cursor: hasAssets ? 'pointer' : 'default',
          '&:hover': hasAssets ? { backgroundColor: 'rgba(255,255,255,0.05)' } : {},
        }}
        onClick={() => {
          if (!hasAssets) return;
          const next = !expanded;
          setExpanded(next);
          try { localStorage.setItem('assetrac-inventory-panel-open', String(next)); } catch { /* non-fatal */ }
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: '0.5px', fontSize: '0.875rem' }}>
          INVENTORY
        </Typography>
        <IconButton 
          size="small" 
          sx={{ p: 0 }} 
          disabled={!hasAssets}
          aria-label={expanded ? 'Collapse inventory panel' : 'Expand inventory panel'}
        >
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded && hasAssets}>
        <Box sx={{ px: 2, pb: 2 }}>
          {showEntriesError && (
            <Box
              role="alert"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}
            >
              <Typography variant="caption" sx={{ flexGrow: 1, color: 'warning.main' }}>
                Manual counts unavailable. {entriesError}
              </Typography>
              <Typography
                component="button"
                type="button"
                variant="caption"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(fetchEntries({ projectId, filters: {} }));
                }}
                aria-label="Retry loading manual counts"
                sx={{
                  background: 'none', border: 0, p: 0, cursor: 'pointer',
                  color: 'warning.main', fontWeight: 700, textDecoration: 'underline',
                }}
              >
                Retry
              </Typography>
            </Box>
          )}
          {/* ASS-77: MAT (manual) row, parallel to (not merged into) the
              beacon/equipment typeGroups below. Rendered first since it's
              the manual-tracking surface's primary content. */}
          {matGroup && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 4px 0px 4px',
                  minHeight: 40,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
                onClick={() => toggleType(matGroup.name)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ChevronIcon
                    sx={{
                      fontSize: 16,
                      transform: expandedTypes[matGroup.name] ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s',
                      color: 'text.secondary',
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mr: `${SPACING.xs}px` }}>
                  <SpriteIcon iconName="Mat_Marker" size={22} />
                </Box>

                <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                  {matGroup.name.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 36, textAlign: 'right' }}>
                  {matGroup.count}
                </Typography>
              </Box>

              <Collapse in={expandedTypes[matGroup.name]}>
                {matGroup.rows.map((row) => {
                  const tip = rowTooltip(row);
                  const line = (
                    <Box
                      key={row.key}
                      data-testid={`scoreboard-row-${row.key}`}
                      // SB-6: focusable so a tap (mobile) or keyboard can open
                      // the tooltip, not just a desktop hover.
                      tabIndex={tip ? 0 : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0px 4px 0px 20px',
                        cursor: tip ? 'help' : 'default',
                        outline: 'none',
                        '&:focus-visible': { backgroundColor: 'rgba(255,255,255,0.08)' },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {row.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
                        {row.count}
                      </Typography>
                    </Box>
                  );
                  if (!tip) return line;
                  return (
                    <Tooltip
                      key={row.key}
                      placement="right"
                      enterTouchDelay={0}
                      leaveTouchDelay={4000}
                      title={(
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{tip.title}</Typography>
                          {tip.description && (
                            <Typography variant="caption" sx={{ display: 'block' }}>{tip.description}</Typography>
                          )}
                        </Box>
                      )}
                    >
                      {line}
                    </Tooltip>
                  );
                })}
              </Collapse>
            </Box>
          )}

          {typeGroups.map((group) => (
            <Box key={group.name}>
              {/* Type row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 4px 0px 4px',
                  minHeight: 40,
                  cursor: Object.keys(group.subtypes).length > 0 ? 'pointer' : 'default',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
                onClick={() => Object.keys(group.subtypes).length > 0 && toggleType(group.name)}
              >
                {/* Expand arrow for subtypes */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {Object.keys(group.subtypes).length > 0 && (
                    <ChevronIcon
                      sx={{
                        fontSize: 16,
                        transform: expandedTypes[group.name] ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.2s',
                        color: 'text.secondary',
                      }}
                    />
                  )}
                </Box>

                {/* Icon */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mr: `${SPACING.xs}px` }}>
                  <SpriteIcon iconName={group.iconName} size={22} />
                </Box>

                {/* Name and count */}
                <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                  {group.name.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 36, textAlign: 'right' }}>
                  {group.count}
                </Typography>
              </Box>

              {/* Subtypes (expandable) */}
              <Collapse in={expandedTypes[group.name]}>
                {Object.entries(group.subtypes).map(([subName, subCount]) => (
                  <Box
                    key={subName}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0px 4px 0px 20px',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                      {subName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
                      {subCount}
                    </Typography>
                  </Box>
                ))}
              </Collapse>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ProjectInventoryPanel;
